import { EventSource } from 'eventsource'

import { config } from '@/config'
import { ensureFreshToken } from '@/config/keycloakClient'
import { apiService } from '@/service/api'
import { readSSEMessages } from '@/utils/sse'

import type {
  ExtractedDocumentText,
  GenerateWorkflowDraftRequest,
  GenerateWorkflowDraftResult,
  ResumeWorkflowRunRequest,
  StartWorkflowRunRequest,
  StartWorkflowRunResponse,
  WorkflowControlFlowGraph,
  WorkflowDefinition,
  WorkflowKgReadinessResult,
  WorkflowPlanPreview,
  WorkflowProgressFrame,
  WorkflowRunView,
  WorkflowStepRunView,
  WorkflowTemplateSummary,
} from './workflowTypes'

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await apiService.fetchOrError(apiService.getUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return response.json()
}

/**
 * Multipart POST. No `Content-Type` header on purpose — the browser sets it with the boundary, and
 * setting it by hand produces a body the server cannot split.
 */
async function postForm<T>(path: string, form: FormData): Promise<T> {
  const response = await apiService.fetchOrError(apiService.getUrl(path), {
    method: 'POST',
    body: form,
  })
  return response.json()
}

async function getJson<T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): Promise<T> {
  const response = await apiService.fetchOrError(
    apiService.getUrl(path, params)
  )
  return response.json()
}

async function sendJson<T>(method: 'PATCH' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const response = await apiService.fetchOrError(apiService.getUrl(path), {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
  return response.json()
}

/**
 * Generate a workflow draft — over SSE, because a slow generation used to be DISCARDED.
 *
 * The plain POST this replaces is one long request/response, and Kong cuts an idle connection at 60s.
 * Draft generation is the largest single model call in the product (a 26B writing a whole multi-step
 * workflow), so it routinely runs past that. Measured on dev, 2026-08-07 — every one of these returned
 * HTTP 200 from the server:
 *
 *     4.2s · 19.3s · 22.5s · 29.6s · 40.9s · 57.8s │ 90.7s · 186.3s · 375.5s
 *                                          ^^^^^ 2s of margin     ^^^^^^ cut
 *
 * Everything left of the bar reached the author. Everything right of it did not: the browser saw a
 * network error, this module's caller fell back to the canonical troubleshooting skeleton, and the user
 * was told "I couldn't generate a custom workflow from that" — blaming their prompt for a gateway
 * timeout, while the server finished a real draft and returned it to a socket nobody was holding. The
 * 29.6s success and the 375.5s failure were the same feature working and not working purely on length.
 *
 * The heartbeat frames the server interleaves keep the connection non-idle, so Kong leaves it alone and
 * a 6-minute generation lands. Same fix, same reason, as `workflow-progress-stream.ts` and
 * `files.service.ts`.
 *
 * SAME SIGNATURE as the POST it replaces — the terminal frame carries exactly the old response body —
 * so every caller is unchanged and the builder's planning stages keep working as they do today.
 *
 * The frame protocol is deliberately minimal: `heartbeat` (keep the connection alive), then exactly one
 * `result` or `error`. REAL staged progress would be a genuine improvement over the builder's current
 * animation, but it needs `generateDraft` to take a progress callback and report its actual phases —
 * a separate change, and not what the timeout needs.
 */
/**
 * Four heartbeats of silence. The server beats every 15 s, so a genuinely working generation can
 * never reach this — while a stream that stalls mid-flight (pod evicted, socket black-holed) fails in
 * a minute instead of leaving "Generating…" on screen forever, which is the same indefinite wait this
 * route was added to remove, only harder to notice.
 */
const DRAFT_STREAM_INACTIVITY_TIMEOUT_MS = 60_000

export async function generateWorkflowDraft(
  body: GenerateWorkflowDraftRequest & {
    /** Opt into the Cursor-style clarify pass (questions instead of a draft). */
    clarify?: boolean
    /** Answers to a prior clarify round; folded into the generation prompt. */
    clarificationAnswers?: Array<{ question: string; answer: string }>
    /** False from `/workflow/new` (no templateId): do not reopen a tenant workflow. */
    reuseExisting?: boolean
  }
): Promise<GenerateWorkflowDraftResult> {
  let response: Response
  try {
    response = await apiService.fetchOrError(
      apiService.getUrl('v2/kg-agent/workflows/drafts/generate/stream'),
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify(body),
      },
      // An SSE stream: the response is held open for the length of the
      // generation, so a request deadline would cut it off mid-draft. Liveness
      // is enforced instead by `readSSEMessages`'s per-message inactivity
      // timeout below (DRAFT_STREAM_INACTIVITY_TIMEOUT_MS), which is the right
      // shape for a stream — silence is the failure, duration is not.
      { timeoutMs: null }
    )
  } catch (err) {
    // DEPLOY SKEW: this bundle can be live against a backend that predates the stream route. A 404/405
    // there is not a generation failure and must not surface as one — fall back to the plain POST,
    // which is still mounted and returns the identical body. Only the route's ABSENCE is caught; a real
    // rejection (401, 400 from the injection scan, 500) rethrows, because the caller's error banner and
    // its canonical-skeleton fallback are the right handling for those.
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status !== 404 && status !== 405) throw err
    return postJson('v2/kg-agent/workflows/drafts/generate', body)
  }
  let result: GenerateWorkflowDraftResult | null = null
  for await (const message of readSSEMessages(response, {
    inactivityTimeoutMs: DRAFT_STREAM_INACTIVITY_TIMEOUT_MS,
  })) {
    if (!message.data) continue
    let frame: { kind?: string; draft?: GenerateWorkflowDraftResult; message?: string }
    try {
      frame = JSON.parse(message.data)
    } catch {
      // A malformed frame is not a reason to lose a generation that may still be running; the terminal
      // frame is what decides the outcome, and its absence is caught below.
      continue
    }
    // The server relays a failure as a FRAME, not a status code — its headers went out before the work
    // started, so there is no code left to send. Rethrowing here keeps the caller's existing catch
    // (which surfaces the real cause and offers the canonical skeleton) working unchanged for a
    // GENUINE failure, which is what that fallback was always for.
    if (frame.kind === 'error') throw new Error(frame.message ?? 'Workflow draft generation failed.')
    if (frame.kind === 'result' && frame.draft) result = frame.draft
  }
  // A stream that ended with no terminal frame is a real failure (the pod died, or the socket was cut
  // anyway) and must not read as success — silently returning null here would hand the caller an empty
  // draft, which is a worse version of the bug this replaced.
  if (!result) throw new Error('Workflow draft generation ended without a result.')
  return result
}

/**
 * Which engine a run started now would use, from the SAME flag read the start path makes.
 *
 * Asked rather than evaluated client-side because the frontend's PostHog identity is
 * not the backend's. Send no longer branches on this: both engines draft first, and
 * the generate-draft route draws v1 or the v2 plan from the same flag.
 *
 * The hook's `engine` is `null` until this round-trip lands.
 */
export function getWorkflowEngine(): Promise<{ engine: 'v1' | 'v2' }> {
  return getJson('v2/kg-agent/workflows/engine')
}

export function listWorkflowTemplates(
  status?: 'draft' | 'published' | 'archived'
): Promise<{ templates: WorkflowTemplateSummary[] }> {
  return getJson('v2/kg-agent/workflows/templates', status ? { status } : undefined)
}

/** List workflow runs (tenant-wide; optionally filtered). Backs the Report tab. */
export function listWorkflowRuns(params?: {
  conversationId?: string
  templateId?: string
  status?: string
  limit?: number
}): Promise<{ runs: WorkflowRunView[] }> {
  return getJson('v2/kg-agent/workflows/runs', {
    conversationId: params?.conversationId,
    templateId: params?.templateId,
    status: params?.status,
    limit: params?.limit,
  })
}

/**
 * Fetch the leading text of a catalog document to use as a workflow RECIPE
 * (recipe source 2). Concatenates the first few pages so the generator sees the
 * procedure, not just the title page.
 */
/**
 * Diagnostic sentences the page-text endpoint returns INSTEAD OF page text.
 *
 * `get_page_text` answers a page it cannot extract with a human-readable
 * explanation rather than an empty string — "Page 2 content not available…",
 * "Page 2: Document is being processed. Please wait…", "Page 2: This is a
 * P&ID/PFD diagram…". That is the right answer for a VIEWER, which shows the
 * sentence to a reader, and the wrong one here: this text is compiled into a
 * PROCEDURE, so a diagnostic joined into it becomes part of the method the run
 * executes.
 *
 * It bites hardest on a born-markdown document — a call transcript, which is
 * exactly what the interview route supplies. Those carry no `## Page N` markers,
 * so the endpoint returns the whole body for page 0 and one of these sentences
 * for pages 1 and 2; a three-page read appended two lines of troubleshooting
 * advice to what an expert actually said, and nothing failed loudly.
 *
 * Matched on the client because the endpoint cannot tell the two cases apart for
 * us: the same string is content to a viewer and noise to a compiler. Every
 * pattern below is transcribed from a `return` in
 * `backend/core/src/intai/document_catalog/document_index.py`
 * (lines 1881-1888 and 2031-2188) — if that list grows, this one has to follow.
 */
const PAGE_TEXT_SENTINELS: readonly RegExp[] = [
  // 1881 / 1884 / 1888 — whole-document diagnostics
  /^Document (?:is being (?:processed|uploaded)|text not available)\b/i,
  // 2031 / 2188 — "Page N content|text not available"
  /^Page \d+:? (?:content|text) not available\b/i,
  // 2065 / 2178 / 2183 — "Page N: Document is being…/content is loading"
  /^Page \d+: Document (?:is being (?:processed|uploaded)|content is loading)\b/i,
  // 2071 / 2099 — diagram pages have no text layer at all
  /^Page \d+: This (?:is a|page is a) P&ID\b/i,
]

/**
 * Longest diagnostic the endpoint emits is ~180 characters (the P&ID one at
 * `document_index.py:2071`). A page that OPENS with one of those phrases but runs on for
 * paragraphs is real content quoting it, not a diagnostic — so length is the tiebreaker rather
 * than the prefix alone (greptile P2 on #29841: an overbroad prefix could swallow a genuine page
 * and leave the procedure incomplete).
 */
const MAX_SENTINEL_CHARS = 240

const isPageTextSentinel = (text: string): boolean =>
  text.length <= MAX_SENTINEL_CHARS && PAGE_TEXT_SENTINELS.some((pattern) => pattern.test(text))

export async function fetchDocumentRecipeText(documentId: string, pages = 3): Promise<string> {
  const texts = await Promise.all(
    // getDocumentPageText is 0-indexed (PdfViewer passes `p - 1`); fetch pages
    // 0..pages-1. Previously `i + 1` skipped the first page and over-fetched one.
    Array.from({ length: Math.max(1, pages) }, (_, i) =>
      apiService.getDocumentPageText(documentId, i).catch(() => '')
    )
  )
  // Pages carrying no usable text are SKIPPED, not kept: a document shorter than
  // the read left empty slots in the join, and a page the endpoint could not
  // extract contributed its own explanation. Skipped rather than stopping at the
  // first gap, so a diagram page between two text pages cannot truncate the read.
  return texts
    .map((text) => text.trim())
    .filter((text) => text.length > 0 && !isPageTextSentinel(text))
    .join('\n\n')
    .trim()
}

export interface WorkflowTemplateRow {
  templateId: string
  version: string
  status: string
  /** Full definition as persisted — the server stamps id/version/title into it
   *  (an upsert over an immutable row forks + re-versions), so this copy is the
   *  authoritative one for state adoption after a save. */
  definition?: WorkflowDefinition
}

/**
 * Create-or-overwrite a DRAFT row (generation auto-save + "Save as draft").
 * A regenerated draft replaces its earlier auto-save at the same id@version;
 * a published version at that key still 409s (immutable).
 */
export function upsertWorkflowDraft(definition: WorkflowDefinition): Promise<WorkflowTemplateRow> {
  return sendJson('PUT', 'v2/kg-agent/workflows/templates/draft', { definition })
}

/**
 * Enhance an instruction/prompt with the native synthesizer. `enhanced` is
 * null when no model is configured or the rewrite adds nothing.
 */
export function enhanceWorkflowInstructions(
  instructions: string
): Promise<{ enhanced: string | null }> {
  return postJson('v2/kg-agent/workflows/drafts/enhance', { instructions })
}

/**
 * Save a workflow as a new published VERSION under a stable id (builder "Save").
 * Pass the workflow's current id to version it (v1→v2…); omit (or pass a
 * built-in id) to fork a fresh stable id. Returns the resolved id + version.
 */
export function saveWorkflow(body: {
  templateId?: string
  title: string
  definition: WorkflowDefinition
}): Promise<{ templateId: string; version: string }> {
  return postJson('v2/kg-agent/workflows/templates/save', body)
}

/** Server-persisted per-workflow settings row (Settings panel / scheduler). */
export interface WorkflowSettingsRow {
  tenantId: string
  templateId: string
  active: boolean
  frequency: string
  timeOfDay: string
  maxSteps: number
  instructions: string
  engineers: string[]
  knowledgeFiles: string[]
  notifyPeople: string[]
  limits: {
    workflowSpendUsd?: number | null
    notifyAtUsd?: number[]
    perRunSpendUsd?: number | null
  }
  onErrorByStep: Record<string, string>
}

export function getWorkflowTemplateSettings(templateId: string): Promise<WorkflowSettingsRow> {
  return getJson(`v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/settings`)
}

export function updateWorkflowTemplateSettings(
  templateId: string,
  patch: Record<string, unknown>
): Promise<WorkflowSettingsRow> {
  return sendJson('PUT', `v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/settings`, patch)
}

/** The most recent run for a workflow (with chat), or null — used to reopen it. */
export function getLatestRunForTemplate(
  templateId: string
): Promise<{ run: WorkflowRunView | null }> {
  return getJson(`v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/latest-run`)
}

/** Publish a draft template version so it becomes runnable by id. */
export function publishWorkflowTemplate(
  templateId: string,
  version: string
): Promise<WorkflowTemplateRow> {
  return postJson(
    `v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(version)}/publish`,
    {}
  )
}

export function previewWorkflowTemplate(
  templateId: string,
  body: { mode?: 'lite' | 'full' }
): Promise<WorkflowPlanPreview> {
  return postJson(`v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/preview`, body)
}

/** Read-only control-flow graph (nodes + decision/fanout/retry edges) for the flowchart view. */
export function getWorkflowTemplateGraph(
  templateId: string
): Promise<WorkflowControlFlowGraph> {
  return getJson(`v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/graph`)
}

/** Fetch a template's full definition (used to duplicate it). */
export function getWorkflowTemplateDefinition(templateId: string): Promise<WorkflowDefinition> {
  return getJson(`v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}`)
}

/** Archive a template VERSION (removed from the published/live set; kept for history). */
export function archiveWorkflowTemplate(
  templateId: string,
  version: string
): Promise<WorkflowTemplateRow> {
  return postJson(
    `v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/versions/${encodeURIComponent(version)}/archive`,
    {}
  )
}

/** Rename a tenant workflow (display title). Built-ins are read-only (server rejects). */
export function renameWorkflowTemplate(templateId: string, title: string): Promise<WorkflowTemplateRow> {
  return sendJson('PATCH', `v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}`, { title })
}

/** Delete every version of a tenant workflow. Built-ins are read-only (server rejects). */
export function deleteWorkflowTemplate(templateId: string): Promise<{ deleted: number }> {
  return sendJson('DELETE', `v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}`)
}

/**
 * Duplicate any template (built-in or tenant workflow) into a new published
 * tenant workflow. Server-side trusted copy — works for verify/fanout templates
 * that the authoring endpoint would reject.
 */
export function duplicateWorkflowTemplate(templateId: string, title?: string): Promise<WorkflowTemplateRow> {
  return postJson(`v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/duplicate`, { title })
}

export function checkTroubleshootingReadiness(
  problem: string
): Promise<WorkflowKgReadinessResult> {
  return postJson('v2/kg-agent/workflows/troubleshooting/readiness', {
    problem,
  })
}

export async function createWorkflowConversation(body: {
  contextId: string
  contextType: 'workspace' | 'document'
}): Promise<string> {
  const response = await postJson<{ conversation: { id: string } }>('v1/conversations', {
    context_id: body.contextId,
    context_type: body.contextType,
  })
  return response.conversation.id
}

export function startWorkflowRun(
  conversationId: string,
  body: StartWorkflowRunRequest
): Promise<StartWorkflowRunResponse> {
  return postJson(
    `v2/chat/conversations/${conversationId}/workflows/runs`,
    body
  )
}

export function getWorkflowRun(runId: string): Promise<WorkflowRunView> {
  return getJson(`v2/kg-agent/workflows/runs/${runId}`)
}

export function getWorkflowRunSteps(
  runId: string
): Promise<{ steps: WorkflowStepRunView[] }> {
  return getJson(`v2/kg-agent/workflows/runs/${runId}/steps`)
}

export function cancelWorkflowRun(runId: string): Promise<WorkflowRunView> {
  return postJson(`v2/kg-agent/workflows/runs/${runId}/cancel`, {})
}

/** Persist the builder chat transcript on the run so it survives reload. */
export function saveWorkflowRunChat(
  runId: string,
  chatLog: unknown[]
): Promise<{ saved: number }> {
  return sendJson('PATCH', `v2/kg-agent/workflows/runs/${encodeURIComponent(runId)}/chat`, {
    chatLog,
  })
}

/**
 * Pre-run builder chat transcript, keyed by template (a chat-built draft that
 * was never run). Once a run exists the transcript persists per-run via
 * saveWorkflowRunChat and the builder restores from the run instead.
 */
export function getWorkflowTemplateChat(templateId: string): Promise<{ chatLog: unknown[] }> {
  return getJson(`v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/chat`)
}

export function saveWorkflowTemplateChat(
  templateId: string,
  chatLog: unknown[]
): Promise<{ saved: number }> {
  return sendJson(
    'PATCH',
    `v2/kg-agent/workflows/templates/${encodeURIComponent(templateId)}/chat`,
    { chatLog }
  )
}

/** Advance a run parked in `awaiting_approval` (the pre-run HITL gate). */
export function approveWorkflowRun(runId: string): Promise<WorkflowRunView> {
  return postJson(`v2/kg-agent/workflows/runs/${encodeURIComponent(runId)}/approve`, {})
}

/** Re-execute an unsuccessful run. v1 resumes from the failed step; v2 re-runs the stored plan. */
export function retryWorkflowRun(runId: string): Promise<WorkflowRunView> {
  return postJson(`v2/kg-agent/workflows/runs/${encodeURIComponent(runId)}/retry`, {})
}

/** Resume a `paused` run (operator pause). */
export function unpauseWorkflowRun(runId: string): Promise<WorkflowRunView> {
  return postJson(`v2/kg-agent/workflows/runs/${encodeURIComponent(runId)}/unpause`, {})
}

export function resumeWorkflowRun(
  runId: string,
  body: ResumeWorkflowRunRequest
): Promise<WorkflowRunView> {
  return postJson(`v2/kg-agent/workflows/runs/${runId}/resume`, body)
}

/**
 * Read a document's text WITHOUT indexing it.
 *
 * `POST /v1/document/extract-text` parses and returns; nothing is stored, nothing is queued. That
 * is the whole reason this is not `apiService.uploadFile`, which enqueues the ingestion pipeline
 * and leaves the caller polling for minutes before the text exists.
 */
export async function extractDocumentText(file: File): Promise<ExtractedDocumentText> {
  const form = new FormData()
  form.append('file', file)
  return postForm('v1/document/extract-text', form)
}

export interface WorkflowProgressSubscription {
  /** Close the stream and stop receiving frames. Idempotent. */
  close: () => void
}

/**
 * Subscribe to a run's live progress over SSE
 * (`GET v2/kg-agent/workflows/runs/:runId/progress`). Uses the `eventsource`
 * polyfill with a Bearer-token `fetch` (the browser EventSource can't set auth
 * headers), mirroring DocumentStatusStreamManager.
 *
 * The backend closes the stream when the run is terminal or parked; we surface
 * the final frame and then call `onClose('complete')`. Any transport failure
 * calls `onClose('error')` so the caller can fall back to polling — this helper
 * deliberately does NOT poll itself, keeping the fallback policy with the hook
 * that owns the run's full data model.
 */
export function subscribeWorkflowRunProgress(
  runId: string,
  handlers: {
    onFrame: (frame: WorkflowProgressFrame) => void
    onClose?: (reason: 'complete' | 'error') => void
  }
): WorkflowProgressSubscription {
  let source: EventSource | null = null
  let closed = false

  const close = () => {
    if (closed) return
    closed = true
    source?.close()
    source = null
  }

  const fail = () => {
    if (closed) return
    close()
    handlers.onClose?.('error')
  }

  void (async () => {
    const token = await ensureFreshToken().catch(() => null)
    if (closed) return
    if (!token) {
      fail()
      return
    }
    const url = `${config.apiBaseUrl}/v2/kg-agent/workflows/runs/${runId}/progress`
    try {
      source = new EventSource('', {
        fetch: (_input, init) => {
          // `eventsource` passes a plain Record today, but spread on a native
          // Headers instance yields `{}` and silently drops Accept: text/event-stream.
          const existingHeaders =
            init?.headers instanceof Headers
              ? Object.fromEntries(init.headers.entries())
              : ((init?.headers as Record<string, string> | undefined) ?? {})
          return fetch(url, {
            ...init,
            headers: { ...existingHeaders, Authorization: `Bearer ${token}` },
          })
        },
      })
    } catch {
      fail()
      return
    }

    source.onmessage = (event: MessageEvent) => {
      let frame: WorkflowProgressFrame
      try {
        const parsed = JSON.parse(event.data) as WorkflowProgressFrame | { heartbeat: true }
        // Server keepalive so Kong doesn't idle-kill the stream during long
        // quiet agentic steps — bytes on the wire, not a frame. Drop it.
        if ('heartbeat' in parsed) return
        frame = parsed
      } catch {
        return
      }
      handlers.onFrame(frame)
      // The server completes the stream after a terminal/parked frame; close our
      // side too so a lingering connection doesn't trip onerror→fallback.
      if (frame.terminal || frame.parked) {
        close()
        handlers.onClose?.('complete')
      }
    }

    source.onerror = () => fail()
  })()

  return { close }
}
